import { ExpandMoreRounded as ExpandMoreRoundedIcon } from 'components/icons';
import { DataTable, type DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { useListTableFilter } from 'components/tables/list-table/useListTableFilter';
import { useRowLink } from 'components/tables/list-table/useRowLink';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyGetMattersWithFiltersQuery } from 'store/api/matters';
import { MatterListItem } from 'types/matters';
import { cn } from 'utils/cn';
import type { SearchBarSelector } from 'utils/searchQueryToRsql';

// Hosts stack several of these as one bordered group: the group's first and last item carry its
// rounded corners, and neighbours share a single border line.
export const ROOT_CLASS =
    'bg-paper border-divider overflow-hidden border first-of-type:rounded-t-md last-of-type:rounded-b-md not-last-of-type:border-b-0';
export const SUMMARY_CLASS =
    'bg-grey-50 text-body1 text-fg flex min-h-10 w-full items-center px-4 py-1.25 font-semibold';
// A one-row grid animating 0fr to 1fr follows its content's live height, so rows replacing the loading skeleton
// mid-animation are not clipped to the skeleton's height and then snapped. `self-start` keeps the content at its
// own height; `min-h-0` lets the row close to nothing.
export const PANEL_CLASS =
    'grid grid-rows-[1fr] overflow-hidden transition-[grid-template-rows] duration-250 ease-in-out *:min-h-0 *:self-start data-ending-style:grid-rows-[0fr] data-starting-style:grid-rows-[0fr] motion-reduce:transition-none';

// The case-state counters over these lists send the same search, so their counts match the rows.
export const MATTER_SEARCH_BAR_SELECTORS: SearchBarSelector[] = [
    { selector: 'name' },
    { selector: 'caseNumber' },
    { selector: 'matterClientReference' },
    { selector: 'responsibleEmployee.fullName' },
    { selector: 'supervisedByEmployee.fullName' },
    { selector: 'clientParty.name' },
    { selector: 'handledByOffice.name' },
];

type Props = {
    accordionTitle: JSX.Element;
    count: number;
    passiveFilter: string;
    columns: DataTableColumnDef<MatterListItem>[];
    /** Managed-layout key. Each host passes its own column set, so each gets its own id. */
    columnConfigId: string;
    searchText: string;
    defaultExpanded: boolean;
    defaultFetched: boolean;
    rowsPerPage?: number;
    /** About what one of the host's rows measures, so the rows not measured yet start near their real height. */
    estimatedRowHeight?: number;
    disableUrlState?: boolean;
    onDataChange?: (data: MatterListItem[]) => void;
    /** The row's case, opened by a click on the row in the new design. Omitted, the rows are not clickable. */
    getRowHref?: (matter: MatterListItem) => string | undefined;
};

const MattersAccordionTable = (props: Props): JSX.Element => {
    const {
        accordionTitle,
        count,
        passiveFilter,
        searchText,
        rowsPerPage = 10,
        estimatedRowHeight,
        disableUrlState = false,
        defaultExpanded,
        defaultFetched,
        columns,
        columnConfigId,
        onDataChange,
        getRowHref,
    } = props;
    const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
    const [isFetchEnabled, setIsFetchEnabled] = useState<boolean>(defaultFetched);
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- previous-prop tracker; read during render at `if (defaultExpanded !== prevDefaultExpanded)`
    const [prevDefaultExpanded, setPrevDefaultExpanded] = useState<boolean>(defaultExpanded);
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- previous-prop tracker; read during render at `if (defaultFetched !== prevDefaultFetched)`
    const [prevDefaultFetched, setPrevDefaultFetched] = useState<boolean>(defaultFetched);

    // Storing previous prop value during render avoids the derived-state useEffect anti-pattern.
    // See https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
    if (defaultExpanded !== prevDefaultExpanded) {
        setPrevDefaultExpanded(defaultExpanded);
        setIsExpanded(defaultExpanded);
    }
    if (defaultFetched !== prevDefaultFetched) {
        setPrevDefaultFetched(defaultFetched);
        setIsFetchEnabled(defaultFetched);
    }

    const handleExpand = useCallback(() => {
        if (!isExpanded && count > 0) {
            setIsExpanded(true);
        }
    }, [isExpanded, count]);

    const titleWithExpand = React.cloneElement(accordionTitle, { onExpand: handleExpand });

    // Closing unmounts the table, so the offset it was scrolled to is kept here and put back when it reopens.
    const scrollElementRef = useRef<HTMLDivElement | null>(null);
    const scrollTopRef = useRef(0);
    const handleScrollElementChange = useCallback((element: HTMLDivElement | null) => {
        scrollElementRef.current = element;
        if (element) element.scrollTop = scrollTopRef.current;
    }, []);

    const [fetchData, { data, isFetching, isLoading, isUninitialized, error }] = useLazyGetMattersWithFiltersQuery();
    const rowLink = useRowLink(getRowHref);

    const [paginationProps] = useMemo(() => {
        if (!data) return [undefined, undefined];
        const { content, ...rest } = data;
        return [{ ...rest }, content];
    }, [data]);

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: MATTER_SEARCH_BAR_SELECTORS,
            passiveFilter,
        }),
        [passiveFilter]
    );

    const { isInitialFetchDone, dataToDisplay, handleLoadMore, handleNewSearch } = useListTableFilter<MatterListItem>({
        fetchData,
        data,
        filterConfig,
        rowsPerPage,
        disableUrlState,
        disableFetch: !isFetchEnabled,
    });

    // TODO: effect-event-handler skip — coalesced reaction to searchText prop and isInitialFetchDone async signal; not driven by a single event.
    useEffect(() => {
        if (isInitialFetchDone) {
            handleNewSearch(searchText);
        }
    }, [handleNewSearch, isInitialFetchDone, searchText]);

    // TODO: no-prop-callback-in-effect — lifts table data to parent; parent (MatterAmountFormTable)
    // would need to read dataToDisplay via a different mechanism (ref, context, or shared store) to remove this.
    useEffect(() => {
        if (onDataChange) {
            onDataChange(dataToDisplay);
        }
    }, [dataToDisplay, onDataChange]);

    if (error) {
        return (
            <div className={ROOT_CLASS}>
                <div className={cn(SUMMARY_CLASS, 'opacity-50')}>
                    <FormattedMessage id={'matters.error-loading-matters-list'} />
                </div>
            </div>
        );
    }

    const isOpen = count > 0 && isExpanded;

    return (
        <Collapsible open={isOpen} disabled={count < 1} className={ROOT_CLASS}>
            <CollapsibleTrigger
                nativeButton={false}
                render={<div />}
                onClick={
                    count > 0
                        ? (e) => {
                              const target = e.target as HTMLElement;
                              if (target.closest('.accordion-checkbox')) {
                                  return;
                              }
                              if (isInitialFetchDone) {
                                  setIsFetchEnabled(false);
                              } else {
                                  setIsFetchEnabled(true);
                              }
                              if (isExpanded) scrollTopRef.current = scrollElementRef.current?.scrollTop ?? 0;
                              setIsExpanded(!isExpanded);
                          }
                        : undefined
                }
                className={cn(
                    SUMMARY_CLASS,
                    'focus-visible:ring-ring/50 outline-none focus-visible:ring-3 focus-visible:ring-inset',
                    count > 0 ? 'cursor-pointer' : 'cursor-default opacity-50'
                )}
            >
                <div className="flex grow items-center">{titleWithExpand}</div>
                <ExpandMoreRoundedIcon
                    className={cn('text-fg-secondary transition-transform duration-150', isOpen && 'rotate-180')}
                />
            </CollapsibleTrigger>
            {/* The panel follows the table's height as it opens, so the table is mounted on open (a hidden table
                measures its rows at 0px), sized by its rows rather than by the panel, and draws a skeleton for
                the page not yet fetched. */}
            <CollapsibleContent className={PANEL_CLASS}>
                <div className="flex max-h-[600px] w-full flex-col">
                    {/* The table owns the bounded scroll area inside the 600px box — which is also what
                        makes it the scroller its own infinite scroll pages off. `min-h-0 flex-1` lets it
                        shrink to the box rather than growing with the row count. */}
                    <DataTable<MatterListItem>
                        columns={columns}
                        data={dataToDisplay}
                        {...rowLink}
                        columnConfigId={columnConfigId}
                        isLoading={isUninitialized || isFetching || isLoading}
                        skeletonRows={Math.min(count, rowsPerPage)}
                        estimatedRowHeight={estimatedRowHeight}
                        className="h-auto! min-h-0 flex-1"
                        onScrollElementChange={handleScrollElementChange}
                        onLoadMore={handleLoadMore}
                        hasNextPage={!!paginationProps && !paginationProps.last}
                        isLoadingMore={isFetching || isLoading}
                    />
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};

export default MattersAccordionTable;
